import intersection from 'lodash/intersection';
import type { Permissions } from './permissions.type';

const REFRESH_TKN_KEY = '_analytics';
const REMEMBER_DEVICE_KEY = '_global';

export function isDeviceRemembered(): boolean {
  return Boolean(Number(localStorage.getItem(REMEMBER_DEVICE_KEY)));
}

export function setIsDeviceRemembered(isRemembered = false): void {
  localStorage.setItem(REMEMBER_DEVICE_KEY, String(Number(isRemembered)));
}

/*
 * A flow session is an external user's (an insured's) session in this tab. Its tokens live in
 * sessionStorage only, which is per tab, and never touch localStorage, where a remembered
 * device keeps a staff member's refresh token.
 */
const ACCESS_TKN_KEY = 'atkn';
const SESSION_REFRESH_TKN_KEY = 'rtkn';
const FLOW_REFRESH_TKN_KEY = 'flow_rtkn';

/** Whether this tab holds a flow session. */
export function hasFlowSession(): boolean {
  return Boolean(window.sessionStorage.getItem(FLOW_REFRESH_TKN_KEY));
}

export function retrieveFlowRefreshToken(): string | null {
  return window.sessionStorage.getItem(FLOW_REFRESH_TKN_KEY);
}

/** Stores a flow session's tokens, in sessionStorage only. Starts the flow session in this tab. */
export function storeFlowTokens(access: string, refresh: string): void {
  window.sessionStorage.setItem(ACCESS_TKN_KEY, access);
  // The key a flow user's authenticated pages read, on a device that is not remembered.
  window.sessionStorage.setItem(SESSION_REFRESH_TKN_KEY, refresh);
  window.sessionStorage.setItem(FLOW_REFRESH_TKN_KEY, refresh);
}

/** Ends this tab's flow session, so the tokens that follow are read as a staff session. */
export function endFlowSession(): void {
  window.sessionStorage.removeItem(FLOW_REFRESH_TKN_KEY);
  window.sessionStorage.removeItem(SESSION_REFRESH_TKN_KEY);
}

/**
 * Stores tokens from a refresh. In a tab with a flow session they stay a flow session: nothing
 * is written to or removed from localStorage.
 */
export function cacheAuthTokens(access: string, refresh: string): void {
  if (hasFlowSession()) {
    storeFlowTokens(access, refresh);
    return;
  }
  window.sessionStorage.setItem(ACCESS_TKN_KEY, access);
  if (isDeviceRemembered()) {
    localStorage.setItem(REFRESH_TKN_KEY, refresh);
  } else {
    localStorage.removeItem(REFRESH_TKN_KEY);
    window.sessionStorage.setItem(SESSION_REFRESH_TKN_KEY, refresh);
  }
}

/** Stores the tokens of a staff login. It ends any flow session in this tab first. */
export function cacheStaffLoginTokens(access: string, refresh: string): void {
  endFlowSession();
  cacheAuthTokens(access, refresh);
}

/**
 * The staff session's tokens only, whatever this tab holds as a flow session: a remembered
 * device reads localStorage, any other reads sessionStorage. For code that means the staff
 * session, such as remember-device auto-login.
 */
export function retrieveStaffAuthTokens(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  const refreshToken = isDeviceRemembered()
    ? localStorage.getItem(REFRESH_TKN_KEY)
    : window.sessionStorage.getItem(SESSION_REFRESH_TKN_KEY);
  return {
    accessToken: window.sessionStorage.getItem(ACCESS_TKN_KEY),
    refreshToken,
  };
}

/** The tokens of this tab's session: the flow session's when it holds one, else the staff's. */
export function retrieveAuthTokens(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  const flowRefresh = retrieveFlowRefreshToken();
  if (flowRefresh) {
    return {
      accessToken: window.sessionStorage.getItem(ACCESS_TKN_KEY),
      refreshToken: flowRefresh,
    };
  }
  return retrieveStaffAuthTokens();
}

export function reduceScopes(rawScopes: Record<string, string[]>): string[] {
  return Object.keys(rawScopes).reduce<string[]>((acc, scopeName) => {
    return [...acc, ...rawScopes[scopeName].map((p) => `${scopeName}:${p}`)];
  }, []);
}

const _defaultOptions = { requireAll: false };
determineIfAllowedScopes.DEFAULT_OPTIONS = _defaultOptions;

export function determineIfAllowedScopes(
  scopes: Permissions[],
  allowed: Permissions[] | 'any' | 'none',
  requireAll = _defaultOptions.requireAll
): boolean {
  if (allowed === 'any') return true;
  if (allowed === 'none') return false;
  if (requireAll) return allowed.every((s) => scopes.includes(s));
  return Boolean(intersection(scopes, allowed).length);
}
